/**
 * Taking a body out of the world without unmounting the thing that owns it.
 *
 * Two features need the same four steps: stowing an item in a pocket (design D3 of
 * `pocket-inventory`) and attaching a part to its host (design D7 of this change). Both
 * hand the body back the way a drop does — dynamic again, with its carryable collision
 * groups and no leftover velocity — and only then switch it off, so it is out of the
 * physics world entirely: unhittable, un-raycastable, and still mounted and still
 * registered under its id.
 *
 * The order matters. A body switched off first keeps whatever velocity and body type it
 * had while carried, and comes back kinematic and moving on the frame something enables
 * it again. Velocities set on a kinematic body are discarded, so dynamic comes first.
 *
 * It lives here, in one function, because the stow path and the attach path drifting
 * apart would be a bug nobody sees until an item comes back out of a pocket behaving
 * differently from one that comes off a robot dog.
 */

import type { RapierRigidBody } from '@react-three/rapier';
import { CARRYABLE_GROUPS } from './objectRegistry';

const ZERO = { x: 0, y: 0, z: 0 };

/**
 * @param dynamicType `rapier.RigidBodyType.Dynamic`, passed in rather than imported: the
 * enum comes from the Rapier instance the `useRapier` hook hands the caller.
 */
export function parkBody(
  body: RapierRigidBody,
  dynamicType: Parameters<RapierRigidBody['setBodyType']>[0],
): void {
  body.setBodyType(dynamicType, true);
  body.collider(0).setCollisionGroups(CARRYABLE_GROUPS);
  body.setLinvel(ZERO, true);
  body.setAngvel(ZERO, true);
  body.setEnabled(false);
}
